import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, CheckCircle2, Lock, Loader2, RefreshCw, TicketPercent } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button, Card, Loading, Page, PageHeader } from "@/components/kit";
import { StateBadge } from "@/pages/master/MasterHome";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");
export const PAY_LABEL = { paid: "pago", pending: "pendente", expired: "expirado", failed: "falhou", refunded: "estornado", trial: "teste grátis" };

function PayButton({ disabled, sub }) {
  const qc = useQueryClient();
  const go = (path) => () => api(path, { method: "POST", body: { origin_url: window.location.origin } }).then((r) => { window.location.href = r.checkout_url; }).catch((e) => toast.error(e.message));
  const cancel = useMutation({
    mutationFn: (v) => api("/billing/cancel", { method: "POST", body: { cancel: v } }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["billing"] }); toast.success(r.cancel_at_period_end ? "Cobrança automática será encerrada no fim do período" : "Cobrança automática mantida"); },
    onError: (e) => toast.error(e.message),
  });
  if (sub?.auto_billing) {
    return (
      <div className="rounded-xl p-4 flex flex-col gap-2" style={{ background: "var(--brand-tint)" }} data-testid="billing-auto-active">
        <div className="label">{sub.cancel_at_period_end ? "Cobrança automática encerra no fim do período" : "Cobrança automática ativa"}{sub.subscription_status === "past_due" ? " · pagamento pendente" : ""}</div>
        <div className="caption">{sub.cancel_at_period_end ? "Depois disso, o acesso continua até a data paga." : "O cartão cadastrado é cobrado todo mês, sem você precisar fazer nada."}</div>
        <Button size="sm" variant="ghost" className="self-start" loading={cancel.isPending} onClick={() => (sub.cancel_at_period_end || window.confirm("Encerrar a cobrança automática no fim do período atual?")) && cancel.mutate(!sub.cancel_at_period_end)} data-testid="billing-cancel-button">{sub.cancel_at_period_end ? "Manter cobrança automática" : "Cancelar cobrança automática"}</Button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      <Button icon={RefreshCw} disabled={disabled} onClick={go("/billing/subscribe")} data-testid="billing-subscribe-button">Ativar cobrança automática</Button>
      <Button variant="secondary" icon={CreditCard} disabled={disabled} onClick={go("/billing/checkout")} data-testid="billing-pay-button">Pagar só este mês</Button>
    </div>
  );
}

export default function Billing() {
  const { data, isLoading } = useQuery({ queryKey: ["billing"], queryFn: () => api("/billing") });
  if (isLoading) return <Loading />;
  const t = data.tenant;
  const trial = t.status === "teste";
  const until = trial ? t.trial_until : t.paid_until;
  return (
    <Page narrow>
      <PageHeader title="Assinatura" subtitle={t.name} back="/menu" />
      <Card className="flex flex-col gap-4" data-testid="billing-card">
        <div className="flex items-center gap-3"><div className="h3 flex-1">{data.plan?.name || "Sem plano definido"}</div><StateBadge state={t.state} data-testid="billing-state" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl p-3" style={{ background: "var(--surface-alt)" }}><div className="caption">Mensalidade</div><div className="h2 tnum" data-testid="billing-price">{data.plan ? brl(data.plan.price) : "—"}</div></div>
          <div className="rounded-xl p-3" style={{ background: "var(--surface-alt)" }}><div className="caption">{trial ? (t.state === "vencido" ? "Teste terminou em" : "Teste até") : "Pago até"}</div><div className="h2 tnum" data-testid="billing-until">{fmt(until)}</div></div>
        </div>
        <PayButton disabled={!data.plan?.price} sub={data.subscription} />
        {data.discount && <p className="text-sm rounded-xl p-3 flex items-center gap-2" style={{ background: "var(--brand-tint)" }} data-testid="billing-discount-note"><TicketPercent size={16} className="shrink-0" style={{ color: "var(--brand)" }} /> <span>Desconto garantido: <b>{data.discount.label}</b> · entra sozinho na assinatura</span></p>}
        {trial && !data.subscription?.auto_billing && <p className="caption" data-testid="billing-trial-note">{t.state === "vencido" ? "Seu teste grátis terminou: assine para voltar a cadastrar e editar. Seus dados continuam guardados." : "Assinando com faltando mais de 2 dias de teste, a primeira cobrança só acontece quando o teste terminar."}</p>}
        <p className="caption">Pagamento seguro com cartão · na cobrança automática o cartão é debitado todo mês; no pagamento avulso cada mensalidade libera 30 dias.</p>
      </Card>
      {data.payments.length > 0 && (
        <Card className="mt-5 flex flex-col gap-1" data-testid="billing-history">
          <div className="h3 mb-1">Histórico</div>
          {data.payments.map((p) => <div key={p.session_id} className="flex justify-between text-sm py-1"><span className="caption">{new Date(p.created_at).toLocaleString("pt-BR")}</span><span className="tnum">{brl(p.amount)} · {PAY_LABEL[p.payment_status] || p.payment_status}</span></div>)}
        </Card>
      )}
    </Page>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sid = params.get("session_id");
  const [tries, setTries] = useState(0);
  const { data } = useQuery({ queryKey: ["pay-status", sid, tries], queryFn: () => api(`/payments/status/${sid}`), enabled: !!sid });
  const paid = data?.payment_status === "paid";
  useEffect(() => {
    if (!data || paid || tries >= 15 || ["expired", "failed"].includes(data.payment_status)) return;
    const t = setTimeout(() => setTries((n) => n + 1), 2000);
    return () => clearTimeout(t);
  }, [data, paid, tries]);
  return (
    <Page narrow>
      <Card className="flex flex-col items-start gap-4 mt-10" data-testid="payment-success-card">
        {paid ? <CheckCircle2 size={32} style={{ color: "var(--success)" }} /> : <Loader2 size={32} className="animate-spin" style={{ color: "var(--brand)" }} />}
        <div className="h2" data-testid="payment-status-text">{paid ? "Pagamento confirmado" : tries >= 15 ? "Ainda aguardando a confirmação" : "Confirmando pagamento…"}</div>
        <p className="caption">{paid ? (data?.kind === "subscription" ? "Cobrança automática ativada · o cartão será cobrado todo mês." : "Sua assinatura foi renovada por mais 30 dias.") : "Isso leva alguns segundos. Você pode voltar e conferir em Assinatura."}</p>
        <Button onClick={() => { window.location.href = paid ? "/" : "/billing"; }} data-testid="payment-continue-button">{paid ? "Ir para o painel" : "Ver assinatura"}</Button>
        {!paid && <button className="caption underline" onClick={() => nav("/billing")}>Voltar</button>}
      </Card>
    </Page>
  );
}

export function BlockedScreen({ state }) {
  const { isAdmin, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--surface-alt)" }}>
      <Card className="max-w-md flex flex-col gap-4" data-testid="tenant-blocked-screen">
        <Lock size={30} style={{ color: "var(--danger)" }} />
        <div className="h2">{state === "vencido" ? "Assinatura vencida" : "Acesso bloqueado"}</div>
        <p className="caption">{state === "vencido" ? "O período pago terminou. " : "O acesso deste restaurante foi suspenso pela plataforma. "}{state === "vencido" ? (isAdmin ? "Pague a mensalidade para liberar o sistema na hora." : "Fale com o administrador do restaurante.") : "Entre em contato com o suporte da plataforma para regularizar."}</p>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && state === "vencido" && <Button icon={CreditCard} onClick={() => nav("/billing")} data-testid="blocked-billing-button">Pagar mensalidade</Button>}
          <Button variant="ghost" onClick={logout} data-testid="blocked-logout-button">Sair</Button>
        </div>
      </Card>
    </div>
  );
}
